/**
 * Billing active-session tracking (v1 widget-ometrics.js parity).
 * Counts once per new visitor after 3s visible OR page-level interaction,
 * then POSTs to ochatbot-v2 `/api/v1/widget/views/ometrics-ai`.
 */

import { getCookie, setCookie } from './cookies.js'
import { ensureOmetricsVisitorId } from './ometricsVisitorId.js'

export { ensureOmetricsVisitorId } from './ometricsVisitorId.js'

const ACTIVE_COUNTED_SUFFIX = 'ometricsActiveCounted'
const ENGAGEMENT_MS = 3000
const INTERACTION_EVENTS = ['scroll', 'click', 'keydown', 'pointerdown']

function activeCountedCookieName(masterKey) {
  return `${ACTIVE_COUNTED_SUFFIX}${masterKey}`
}

export function hasActiveSessionBeenCounted(masterKey) {
  if (!masterKey) return true
  return Boolean(getCookie(activeCountedCookieName(masterKey)))
}

export function markActiveSessionCounted(masterKey) {
  if (!masterKey) return
  setCookie(activeCountedCookieName(masterKey), '1', 30)
}

/** Match v1 OmetricsView desktop flag (screen width > 500). */
export function resolveDesktopFlag() {
  if (typeof window === 'undefined') return -1
  const w =
    typeof window.screen?.width !== 'undefined'
      ? window.screen.width
      : typeof window.screen?.w !== 'undefined'
        ? window.screen.w
        : undefined
  if (w === undefined) return -1
  return w > 500 ? 1 : 0
}

/**
 * POST active session to v2 (CI3 ometrics_viewed.php field aliases supported).
 */
export async function recordActiveSessionView({
  apiBaseUrl,
  masterKey,
  visitorId,
  url,
  desktop,
}) {
  const base = (apiBaseUrl || '').replace(/\/$/, '')
  if (!base || !masterKey || !visitorId) return

  const body = {
    ometricsVal: masterKey,
    ometricsID: visitorId,
    url: url || (typeof window !== 'undefined' ? window.location.href : ''),
    desktop: desktop ?? resolveDesktopFlag(),
  }

  const response = await fetch(`${base}/api/v1/widget/views/ometrics-ai`, {
    method: 'POST',
    credentials: 'omit',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(body),
  })

  if (!response.ok && response.status !== 204) {
    throw new Error(
      `active session view failed: ${response.status} ${response.statusText}`,
    )
  }
}

function isPreviewPayload(payload) {
  if (!payload || typeof payload !== 'object') return false
  const mode = payload.previewMode
  return mode === true || mode === 'true' || mode === 1
}

function resolveMasterKeyFromConfig(cfgMasterKey, configPayload) {
  if (cfgMasterKey) return String(cfgMasterKey).trim()
  if (!configPayload || typeof configPayload !== 'object') return ''
  const meta = configPayload.meta
  if (meta?.master_key) return String(meta.master_key).trim()
  if (configPayload.master_key) return String(configPayload.master_key).trim()
  return ''
}

/**
 * Schedule engagement-gated billing count (v1 scheduleActiveSessionCount).
 */
export function scheduleActiveSessionCount({ apiBaseUrl, masterKey }) {
  if (!masterKey || hasActiveSessionBeenCounted(masterKey)) {
    return
  }

  const visitor = ensureOmetricsVisitorId(masterKey)
  if (!visitor?.id || !visitor.wasJustCreated) {
    return
  }

  const visitorId = visitor.id
  let qualified = false
  let visibilityTimer = null

  function cleanupListeners() {
    if (visibilityTimer != null) {
      clearTimeout(visibilityTimer)
      visibilityTimer = null
    }
    for (const eventName of INTERACTION_EVENTS) {
      document.removeEventListener(eventName, onInteraction, true)
    }
    document.removeEventListener('visibilitychange', onVisibilityChange)
    window.removeEventListener('pagehide', onPageHide)
  }

  async function fireCount() {
    if (qualified || hasActiveSessionBeenCounted(masterKey)) {
      cleanupListeners()
      return
    }
    qualified = true
    cleanupListeners()
    try {
      await recordActiveSessionView({
        apiBaseUrl,
        masterKey,
        visitorId,
      })
      markActiveSessionCounted(masterKey)
    } catch (error) {
      console.error('[OmetricsAI] Error tracking active session:', error)
    }
  }

  function onInteraction() {
    void fireCount()
  }

  function onVisibilityChange() {
    if (document.visibilityState === 'hidden' && visibilityTimer != null) {
      clearTimeout(visibilityTimer)
      visibilityTimer = null
    } else if (
      document.visibilityState === 'visible' &&
      visibilityTimer == null &&
      !qualified
    ) {
      visibilityTimer = setTimeout(() => {
        void fireCount()
      }, ENGAGEMENT_MS)
    }
  }

  function onPageHide() {
    if (visibilityTimer != null) {
      clearTimeout(visibilityTimer)
      visibilityTimer = null
    }
  }

  for (const eventName of INTERACTION_EVENTS) {
    document.addEventListener(eventName, onInteraction, true)
  }
  document.addEventListener('visibilitychange', onVisibilityChange)
  window.addEventListener('pagehide', onPageHide)

  if (document.visibilityState === 'visible') {
    visibilityTimer = setTimeout(() => {
      void fireCount()
    }, ENGAGEMENT_MS)
  }
}

/**
 * Entry point after widget config is loaded (loader + optional bundle callers).
 */
export function tryScheduleActiveSessionTracking({
  apiBaseUrl,
  masterKey: cfgMasterKey,
  configPayload,
}) {
  if (typeof window === 'undefined') return

  if (configPayload?.skipPageViewTracking === true) {
    return
  }
  if (isPreviewPayload(configPayload)) {
    return
  }

  const masterKey = resolveMasterKeyFromConfig(cfgMasterKey, configPayload)
  if (!masterKey) {
    return
  }

  if (window.OmetricsAI_PageViewTracked) {
    return
  }
  window.OmetricsAI_PageViewTracked = true

  scheduleActiveSessionCount({ apiBaseUrl, masterKey })
}
